import React from 'react';
import { useNavigate } from 'react-router-dom';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import GradingSelect from '@/components/shared/Grading/Navigation/GradingSelect';
import { IClassroom, IStudent } from '@/components/staffer/Grading/types';
import ReactSelect5, { Option as GradingSelectOption } from '@/components/common/ReactSelect5';
import { gradingNavigationStringParams } from '@/components/staffer/Grading/utils';
import ArrowNavigator from './ArrowNavigator';

const StudentSelect = ({ selectedStudent, navigableStudentOptions, onStudentChange, studentOptionsData }) => {
  const label = 'Student';
  const toggleId = 'student-toggle';

  return (
    <ArrowNavigator
      containerClass="tw-max-w-[250px]"
      currentValue={selectedStudent?.value || ''}
      isDisabled={navigableStudentOptions.length <= 1}
      label={label}
      labelClass="tw-text-xs"
      navigableOptions={navigableStudentOptions}
      toggleId={toggleId}
      onChange={onStudentChange}
    >
      <ReactSelect5
        className="tw-w-full"
        id={toggleId}
        options={studentOptionsData}
        value={selectedStudent}
        onChange={(opt: GradingSelectOption) => onStudentChange(opt.value)}
      />
    </ArrowNavigator>
  );
};

const TopRowFilters = ({ classrooms }) => {
  const { assignment, classroom, isQuestionMode, questionId, setGradingStore, student } = useGradingStore();
  const navigate = useNavigate();
  let selectedClassroom: GradingSelectOption;
  let selectedStudent: GradingSelectOption;
  let studentsOfSelectedClassroom: IStudent[] = [];

  const classroomOptionsData = classrooms.map((option: IClassroom) => {
    const data = {
      isDisabled: option.students.length === 0,
      label: option.full_name,
      value: option.id.toString(),
    };
    const isClassroomForQuestionMode = isQuestionMode && classroom && option.id === classroom.id;
    const isClassroomOfSelectedStudent = !isQuestionMode && student && option.students.find(s => s.id === student.id);

    if (isClassroomForQuestionMode || isClassroomOfSelectedStudent) {
      selectedClassroom = data;
      studentsOfSelectedClassroom = option.students;
    }

    return data;
  });

  const navigableStudentOptions = [];
  const studentOptionsData = studentsOfSelectedClassroom
    .sort((a, b) => a.last_name.localeCompare(b.last_name))
    .map((st) => {
      const data = {
        label: [st.first_name, st.last_name].filter(a => a != null).join(' '),
        value: st.id.toString(),
      };

      navigableStudentOptions.push(data);
      if (!isQuestionMode && st.id === student.id) selectedStudent = data;

      return data;
    });

  const onClassroomChange = (selectedClassroomId) => {
    if (isQuestionMode) {
      const newClassroom = assignment.classrooms.find(c => c.id.toString() === selectedClassroomId);
      // AIDEV-NOTE: null out the student-level state so QuestionResultsBody treats the window
      // between this navigate() and QuestionGradingHooks rewriting the store as a transition
      // (i.e. shows skeletons) instead of rendering the previous classroom's stale answers.
      setGradingStore({
        classroom: newClassroom,
        questionAnswers: null,
        submission: null,
        submissions: [],
      });
      navigate(`/${assignment.id}/questions/${questionId}?${gradingNavigationStringParams(newClassroom.id)}`);
    } else {
      const classroomOption = classrooms.find(c => c.id.toString() === selectedClassroomId);
      const studentId = classroomOption.students[0].id;
      navigate(`/${assignment.id}/students/${studentId}/grading?${gradingNavigationStringParams()}`);
    }
  };

  const onStudentChange = (selectedStudentId) => {
    const newStudent = studentsOfSelectedClassroom.find(s => s.id.toString() === selectedStudentId);
    // AIDEV-NOTE: Update `student` synchronously so the dropdown label reflects the new selection
    // immediately (otherwise it waits for GradingHooks to write the next atomic snapshot). Null
    // out submission + questionAnswers so useIsStudentTransitioning's `questionAnswers == null`
    // branch still fires and the body skeletons until the new student's data loads.
    if (newStudent) {
      setGradingStore({
        questionAnswers: null,
        student: newStudent,
        submission: null,
        submissions: [],
      });
    }
    navigate(`/${assignment.id}/students/${selectedStudentId}/grading?${gradingNavigationStringParams()}`);
  };

  return (
    <>
      <GradingSelect
        aria-label="dropdown-toggle"
        defaultValue={selectedClassroom}
        id="classroom-toggle"
        label="Classroom"
        labelClass="tw-text-xs"
        options={classroomOptionsData}
        selectClass="tw-ml-1"
        onChange={({ value }) => onClassroomChange(value)}
      />

      {!isQuestionMode && (
        <StudentSelect
          navigableStudentOptions={navigableStudentOptions}
          selectedStudent={selectedStudent}
          studentOptionsData={studentOptionsData}
          onStudentChange={onStudentChange}
        />
      )}
    </>
  );
};

export default TopRowFilters;
